import {
  PREFILL_ORDER_API,
  TKV2_ORDER_ATTACHMENT_API,
  ToolkitOrder,
  expected,
} from '@aloft/toolkit-shared';
import { useQueryClient } from '@tanstack/react-query';

import {
  fetchGetSubjectData,
  usePostVacantLandSales,
  usePrefillOrderAPI,
  useUpdateTkv2OrderAPI,
} from '../../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../../hooks/api/toolkitContext.ts';
import { useAnalyzeSubjectImages } from '../components/OrderTable/hooks/useAnalyzeSubjectImages.ts';
import { useUpdateInitialSubjectValues } from '../components/OrderTable/hooks/useUpdateInitialSubjectValues.ts';
import { useUpdateOrderFeatures } from '../components/OrderTable/hooks/useUpdateOrderFeatures.ts';

export const usePrefillOrder = (): ((order: ToolkitOrder) => Promise<void>) => {
  const prefillOrderAPI = usePrefillOrderAPI();
  const updateOrder = useUpdateTkv2OrderAPI();
  const analyzeSubject = useAnalyzeSubjectImages();
  const updateInitialSubject = useUpdateInitialSubjectValues();
  const postVacantLandSales = usePostVacantLandSales();
  const updateOrderFeatures = useUpdateOrderFeatures();
  const qc = useQueryClient();
  const ctx = useToolkitContext();
  const { fetcherOptions } = ctx;

  return async (order) => {
    if (!order.requiresPrefill) {
      return;
    }

    // Step 1: Prefill order subject data
    const prefilledOrder = PREFILL_ORDER_API.POST.RESPONSE.parse(
      await prefillOrderAPI.mutateAsync({ body: { orderID: order.id } }),
    );

    // Step 2A: Create vacant land sales data entry
    try {
      await postVacantLandSales.mutateAsync({
        pathParams: { orderID: prefilledOrder.id },
      });
    } catch (e) {
      // We don't want failure in this step to block the rest of the prefill process, so we catch and log errors here
      console.error(`Failed to create vacant land sales data for order ${prefilledOrder.id}:`, e);
    }

    // Step 2B: Run subject image analysis
    const subjectImageAnalysisPromise =
      prefilledOrder.input.propertyType === 'singleFamilyResidence'
        ? analyzeSubject(order.id)
        : undefined;

    const subjectPromise = fetchGetSubjectData({
      ...fetcherOptions,
      pathParams: { orderID: order.id },
    }).then((res) => TKV2_ORDER_ATTACHMENT_API.SUBJECT_DATA.GET.RESPONSE.parse(res));

    const [subjectImageAnalysis, subject] = await Promise.all([
      subjectImageAnalysisPromise,
      subjectPromise,
    ]);

    // Step 3A: Calculate initial craftsman quality
    const subjectQuality = subjectImageAnalysis?.body.result.overall_class;

    // Step 3C: Update subject with initial craftsman quality value
    const subjectUpdatePromise = updateInitialSubject(
      order.id,
      subject.editableData,
      subjectQuality,
    );

    await Promise.all([subjectUpdatePromise]);

    const { createdAt, id, modifiedAt, subjectAddress, ...restOfOrder } = prefilledOrder;

    // Step 4: mark order as prefilled
    updateOrder.mutate(
      {
        pathParams: { orderID: expected(prefilledOrder.id) },
        body: {
          ...restOfOrder,
          requiresPrefill: false,
        },
      },
      {
        onSuccess: () => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          qc.invalidateQueries({ queryKey: ['api', 'v2', 'orders'] });
        },
      },
    );

    // Step 5: Update the order features based on the subject and comps' features
    await updateOrderFeatures(order.id);
  };
};
